import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdCameraAlt, MdCameraswitch, MdClose } from 'react-icons/md';
import { NESTED_MODAL_Z } from './ModalPortal';

const hasMediaDevices = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';

const makeFileName = () => `camera_${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;

/**
 * "Take Photo" button. Opens a live camera preview, lets the user capture / retake,
 * and hands a File (image/jpeg) to onCapture — the same shape a file <input> produces.
 * Falls back to the device's native camera picker when getUserMedia isn't available.
 */
export default function CameraCapture({ onCapture, label = 'Take Photo', className = '', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [preview, setPreview] = useState(null); // { url, blob }
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fallbackRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const clearPreview = useCallback(() => {
    setPreview((p) => {
      if (p?.url) URL.revokeObjectURL(p.url);
      return null;
    });
  }, []);

  const startStream = useCallback(
    async (mode) => {
      stopStream();
      setError('');
      setStarting(true);
      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: mode } }, audio: false });
        } catch (err) {
          if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') throw err;
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        setError(
          err?.name === 'NotAllowedError'
            ? 'Camera permission was denied. Allow camera access in your browser settings and try again.'
            : 'Unable to access the camera on this device.'
        );
      } finally {
        setStarting(false);
      }
    },
    [stopStream]
  );

  // Start / restart the stream while the modal is open and no photo is being reviewed.
  useEffect(() => {
    if (open && !preview) startStream(facingMode);
    else stopStream();
  }, [open, preview, facingMode, startStream, stopStream]);

  // Always release the camera on unmount.
  useEffect(() => () => stopStream(), [stopStream]);

  const handleOpen = () => {
    if (disabled) return;
    if (!hasMediaDevices()) {
      fallbackRef.current?.click();
      return;
    }
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    stopStream();
    clearPreview();
    setError('');
    setOpen(false);
  }, [stopStream, clearPreview]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, handleClose]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        setPreview({ url: URL.createObjectURL(blob), blob });
      },
      'image/jpeg',
      0.92
    );
  };

  const handleUse = () => {
    if (!preview?.blob) return;
    const file = new File([preview.blob], makeFileName(), { type: 'image/jpeg', lastModified: Date.now() });
    handleClose();
    onCapture?.(file);
  };

  const handleFallbackChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onCapture?.(file);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        title={label || 'Take Photo'}
        aria-label={label || 'Take Photo'}
        className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-primary-300 bg-white text-primary-700 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${className}`}
      >
        <MdCameraAlt className="text-base" />
        {label}
      </button>
      <input ref={fallbackRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFallbackChange} />

      {open &&
        createPortal(
          <div
            className={`fixed inset-0 ${NESTED_MODAL_Z} flex items-center justify-center bg-black/70 p-3`}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (e.target === e.currentTarget) handleClose();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3 className="text-base font-semibold text-gray-800">{label || 'Take Photo'}</h3>
                <button type="button" onClick={handleClose} className="p-1 rounded hover:bg-gray-100 text-gray-600" aria-label="Close camera">
                  <MdClose size={20} />
                </button>
              </div>

              <div className="relative bg-black aspect-[4/3] flex items-center justify-center">
                {preview ? (
                  <img src={preview.url} alt="Captured" className="w-full h-full object-contain" />
                ) : (
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
                )}
                {!preview && starting && <span className="absolute text-white text-sm">Starting camera…</span>}
                {error && <p className="absolute inset-x-4 text-center text-sm text-red-200">{error}</p>}
                <canvas ref={canvasRef} className="hidden" />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                {preview ? (
                  <>
                    <button type="button" onClick={clearPreview} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                      Retake
                    </button>
                    <button type="button" onClick={handleUse} className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700">
                      Use Photo
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'))}
                      disabled={starting}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <MdCameraswitch size={18} /> Switch Camera
                    </button>
                    {error ? (
                      <button
                        type="button"
                        onClick={() => {
                          handleClose();
                          fallbackRef.current?.click();
                        }}
                        className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                      >
                        Use device camera
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleCapture}
                        disabled={starting}
                        className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        <MdCameraAlt size={18} /> Capture
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
